import BaseCheckbox from 'components/Form/Base/BaseCheckbox';
import Field from 'components/Form/Field';
import IconCard from 'components/IconCards';
import KeyValue from 'components/Layout/KeyValue';
import TableStatic from 'components/Table/TableStatic';
import TooltipIcon from 'components/Tooltip/TooltipIcon';
import Value from 'components/Value';
import {FC, ReactNode, useState} from 'react';
import {twMerge} from 'tailwind-merge';
import {t} from 'ttag';

type EnablePaymentsProps = {
    checked?: boolean;
    editable?: boolean;
};
const PaymentCheckbox: FC<EnablePaymentsProps> = ({checked, editable}) => {
    const [isChecked, setIsChecked] = useState(checked);
    const disabled = !editable;
    return (
        <div className="flex items-center gap-2">
            <BaseCheckbox
                checked={isChecked}
                disabled={disabled}
                name="enable_cc_payments"
                onChange={editable ? () => setIsChecked(!isChecked) : undefined}
            />
            <IconCard icon="show-all" />
        </div>
    );
};

type StyledCellProps = {
    children?: ReactNode;
    className?: string;
};
const StyledCell: FC<StyledCellProps> = ({children, className}) => (
    <TableStatic.Cell className={twMerge('max-w-md break-all', className)}>
        {children || '-'}
    </TableStatic.Cell>
);
type SupersendTableProps = {
    supersends?: {
        name?: string;
        processingFee?: string;
        total?: string;
    }[];
};
const SupersendTable: FC<SupersendTableProps> = ({supersends = []}) => {
    return (
        <TableStatic>
            <TableStatic.Header className="text-sm">
                <TableStatic.HeaderRow>
                    <TableStatic.Column>{t`Document`}</TableStatic.Column>
                    <TableStatic.Column>{t`Total`}</TableStatic.Column>
                    <TableStatic.Column>
                        <span className="inline-flex items-center gap-2">
                            {t`Processing Fee (3.6%)`}
                            <TooltipIcon message={t`Paid by you`} size="base" />
                        </span>
                    </TableStatic.Column>
                </TableStatic.HeaderRow>
            </TableStatic.Header>
            <TableStatic.Body className="text-sm">
                {supersends.length === 0 && (
                    <TableStatic.Row>
                        <StyledCell />
                        <StyledCell />
                        <StyledCell />
                    </TableStatic.Row>
                )}
                {supersends.length !== 0 &&
                    supersends.map(({name, processingFee, total}) => (
                        <TableStatic.Row key={name}>
                            <StyledCell>{name}</StyledCell>
                            <StyledCell className="text-right">
                                {total}
                            </StyledCell>
                            <StyledCell className="text-right">
                                {processingFee}
                            </StyledCell>
                        </TableStatic.Row>
                    ))}
            </TableStatic.Body>
        </TableStatic>
    );
};

type AutoChargingInvoiceProps = {
    checked?: boolean;
    editable?: boolean;
    label?: string;
    subtitle?: string;
};
const AutoChargingInvoice: FC<AutoChargingInvoiceProps> = ({
    checked,
    editable,
    label,
    subtitle,
}) => {
    const [isChecked, setIsChecked] = useState(checked);
    const disabled = !editable;
    return (
        <div className="flex gap-2">
            <BaseCheckbox
                checked={isChecked}
                className="mt-1"
                disabled={disabled}
                name="auto_charge_invoices"
                onChange={editable ? () => setIsChecked(!isChecked) : undefined}
            />
            <div>
                <div>{label || '-'}</div>
                <div>{subtitle || '-'}</div>
            </div>
        </div>
    );
};

export type MakeLeapsPaymentsProps = {
    autoChargingInvoice?: AutoChargingInvoiceProps;
    className?: string;
    enablePayments?: EnablePaymentsProps;
    supersendItems?: SupersendTableProps['supersends'];
};

const MakeLeapsPayments: FC<MakeLeapsPaymentsProps> = ({
    autoChargingInvoice,
    className,
    enablePayments,
    supersendItems,
}) => {
    if (!enablePayments) return null;

    const content = (
        <div className="flex flex-col gap-2">
            <PaymentCheckbox
                checked={enablePayments?.checked}
                editable={enablePayments.editable}
            />
            {supersendItems && <SupersendTable supersends={supersendItems} />}
            {autoChargingInvoice && (
                <AutoChargingInvoice
                    checked={autoChargingInvoice?.checked}
                    editable={autoChargingInvoice?.editable}
                    label={autoChargingInvoice?.label}
                    subtitle={autoChargingInvoice?.subtitle}
                />
            )}
        </div>
    );

    return (
        <div className={className}>
            <KeyValue keyWidth={10}>
                <Field
                    label={t`MakeLeaps Payments`}
                    tooltip={{
                        href: 'https://help.makeleaps.jp/ja/articles/3941544-makeleapsカード決済-stripe',
                        message: t`What is MakeLeaps Payments?`,
                    }}
                    type="value"
                >
                    <Value
                        className="h-full items-center"
                        value={{
                            type: 'custom',
                            value: content,
                        }}
                    />
                </Field>
            </KeyValue>
        </div>
    );
};

export default MakeLeapsPayments;
